import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import type { Country } from "./components/Billing";

// ── Utility
const clamp = (v: number, min: number, max: number): number =>
  Math.min(Math.max(v, min), max);

// ── Types
interface Particle {
  x: number;
  y: number;
  r: number;
  dx: number;
  dy: number;
  color: string;
  alpha: number;
}
interface StatItem {
  number: string;
  label: string;
}
interface CategoryItem {
  name: string;
  stats: StatItem[];
}
interface SectionCardProps {
  side: "left" | "right";
  title: string;
  description: string;
  stats?: StatItem[];
  categories?: CategoryItem[];
  ctaLabel: string;
  ctaColor: string;
  icon: string;
  onCta: () => void;
}
interface AnimCounterProps {
  target: number;
  suffix?: string;
  duration?: number;
}
interface TiltCardProps {
  children: React.ReactNode;
  style?: React.CSSProperties;
}
interface NavbarProps {
  activeSection: string;
  setActiveSection: (s: string) => void;
}
interface RoadmapStep {
  num: string;
  icon: string;
  title: string;
  desc: string;
  color: string;
  bg: string;
  border: string;
  tag: string;
}

const handleNavigation = (navigate: any, path: string) => {
  const userId = localStorage.getItem("userId");
  if (userId) {
    navigate(path);
  } else {
    sessionStorage.setItem("redirectPath", path);
    navigate("/whatsapplogin");
  }
};

const ROADMAP_STEPS: RoadmapStep[] = [
  {
    num: "01",
    icon: "🔍",
    title: "Planning",
    desc: "Defines domain, actors, and core objectives from your prompt.",
    color: "#3B6FFF",
    bg: "#EEF3FF",
    border: "#C7D8FF",
    tag: "Analysis",
  },
  {
    num: "02",
    icon: "🛠️",
    title: "Tech Stack",
    desc: "Picks optimal backend, frontend, database, and infra layers.",
    color: "#7C3AED",
    bg: "#F3EEFF",
    border: "#DDD6FE",
    tag: "Architecture",
  },
  {
    num: "03",
    icon: "📋",
    title: "Use Cases",
    desc: "Enumerates every user story and functional flow.",
    color: "#0EA5E9",
    bg: "#EDF7FF",
    border: "#BAE6FD",
    tag: "Requirements",
  },
  {
    num: "04",
    icon: "📜",
    title: "Compliance",
    desc: "Applies RBI/CBUAE/SAMA, KYC/AML, GDPR, PCI-DSS rules automatically.",
    color: "#D97706",
    bg: "#FFFBEB",
    border: "#FDE68A",
    tag: "Regulatory",
  },
  {
    num: "05",
    icon: "📊",
    title: "Reports",
    desc: "Auto-generates regulatory, audit, and analytics reports for compliance and business intelligence.",
    color: "#F59E0B",
    bg: "#FFFBEB",
    border: "#FDE68A",
    tag: "Reporting",
  },
  {
    num: "06",
    icon: "🏗️",
    title: "System Design",
    desc: "Architects modules, APIs, and service boundaries at scale.",
    color: "#059669",
    bg: "#ECFDF5",
    border: "#A7F3D0",
    tag: "Design",
  },
  {
    num: "07",
    icon: "📁",
    title: "Folder Structure",
    desc: "Generates a production-grade directory and file layout.",
    color: "#0891B2",
    bg: "#ECFEFF",
    border: "#A5F3FC",
    tag: "Scaffolding",
  },
  {
    num: "08",
    icon: "✍️",
    title: "Prompt Builder",
    desc: "Compiles a precise generation prompt from all prior context.",
    color: "#9333EA",
    bg: "#FDF4FF",
    border: "#E9D5FF",
    tag: "Synthesis",
  },
  {
    num: "09",
    icon: "⚙️",
    title: "Backend",
    desc: "Generates controllers, services, models, routes, middleware.",
    color: "#DC2626",
    bg: "#FEF2F2",
    border: "#FECACA",
    tag: "Code Gen",
  },
  {
    num: "10",
    icon: "🎨",
    title: "Frontend",
    desc: "Builds UI components, pages, state, and API hooks.",
    color: "#EA580C",
    bg: "#FFF7ED",
    border: "#FED7AA",
    tag: "Code Gen",
  },
  {
    num: "11",
    icon: "🗄️",
    title: "Database",
    desc: "Writes schemas, migrations, seed data, and query optimisations.",
    color: "#16A34A",
    bg: "#F0FDF4",
    border: "#BBF7D0",
    tag: "Code Gen",
  },
  {
    num: "12",
    icon: "🧪",
    title: "Test Cases & Data",
    desc: "Generates test cases and data for validation.",
    color: "#475569",
    bg: "#F4F6FA",
    border: "#D1D9E8",
    tag: "Testing",
  },
];

// ── Greeting Speech
function useGreetingSpeech() {
  const [speaking, setSpeaking] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.speechSynthesis) return;
    const hour = new Date().getHours();

    const timeGreet =
      hour < 12
        ? "Good morning"
        : hour < 17
          ? "Good afternoon"
          : "Good evening";
    const greet = () => {
      const voices = window.speechSynthesis.getVoices();
      const female =
        voices.find((v) =>
          /female|woman|girl|zira|samantha|victoria|karen|moira|tessa|fiona|veena|susan|catherine/i.test(
            v.name,
          ),
        ) ||
        voices.find((v) =>
          v.name.toLowerCase().includes("google uk english female"),
        ) ||
        voices.find(
          (v) => /en/i.test(v.lang) && v.name.toLowerCase().includes("female"),
        ) ||
        voices[0];
      const msg = new SpeechSynthesisUtterance(
        `${timeGreet}! Welcome to OXYBFS dot AI — your intelligent platform for Banking and Insurance. Powered by AI, built for the future.`,
      );
      if (female) msg.voice = female;
      msg.rate = 0.95;
      msg.pitch = 1.1;
      msg.volume = 1;
      msg.onstart = () => setSpeaking(true);
      msg.onend = () => setSpeaking(false);
      msg.onerror = () => setSpeaking(false);
      window.speechSynthesis.speak(msg);
    };
    if (window.speechSynthesis.getVoices().length) {
      greet();
    } else {
      window.speechSynthesis.addEventListener("voiceschanged", greet, {
        once: true,
      });
    }
    return () => {
      window.speechSynthesis.cancel();
      setSpeaking(false);
    };
  }, []);
  return speaking;
}

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < breakpoint : false,
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);
  return isMobile;
}

function useForceBodyDark() {
  useEffect(() => {
    if (typeof document === "undefined") return;
    const prev = document.body.style.background;
    document.body.style.background = "#020414";
    document.body.style.margin = "0";
    const root = document.getElementById("root");
    if (root) {
      root.style.background = "#020414";
      root.style.minHeight = "100vh";
    }
    return () => {
      document.body.style.background = prev;
    };
  }, []);
}

// ── Particle Canvas
function ParticleCanvas() {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    let W = 0,
      H = 0,
      animId = 0;
    const COLORS = ["#00f5ff", "#7c3aed", "#2563eb", "#06b6d4", "#7C3AED"];
    const particles: Particle[] = Array.from({ length: 90 }, () => ({
      x: Math.random() * window.innerWidth,
      y: Math.random() * window.innerHeight,
      r: Math.random() * 1.8 + 0.4,
      dx: (Math.random() - 0.5) * 0.35,
      dy: (Math.random() - 0.5) * 0.35,
      color: COLORS[Math.floor(Math.random() * COLORS.length)],
      alpha: Math.random() * 0.5 + 0.15,
    }));
    const resize = () => {
      W = canvas.width = window.innerWidth;
      H = canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener("resize", resize);
    const draw = () => {
      ctx.clearRect(0, 0, W, H);
      for (const p of particles) {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = p.color;
        ctx.globalAlpha = p.alpha;
        ctx.shadowBlur = 8;
        ctx.shadowColor = p.color;
        ctx.fill();
        p.x += p.dx;
        p.y += p.dy;
        if (p.x < 0 || p.x > W) p.dx *= -1;
        if (p.y < 0 || p.y > H) p.dy *= -1;
      }
      ctx.globalAlpha = 1;
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x,
            dy = particles[i].y - particles[j].y;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d < 90) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(0,245,255,${0.07 * (1 - d / 90)})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }
      animId = requestAnimationFrame(draw);
    };
    draw();
    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener("resize", resize);
    };
  }, []);
  return (
    <canvas
      ref={ref}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        zIndex: 0,
        pointerEvents: "none",
      }}
    />
  );
}

// ── Tilt Card
function TiltCard({ children, style = {} }: TiltCardProps) {
  const ref = useRef<HTMLDivElement>(null);
  const onMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const rx =
      clamp((e.clientY - (r.top + r.height / 2)) / (r.height / 2), -1, 1) * -10;
    const ry =
      clamp((e.clientX - (r.left + r.width / 2)) / (r.width / 2), -1, 1) * 10;
    el.style.transform = `perspective(800px) rotateX(${rx}deg) rotateY(${ry}deg) scale3d(1.03,1.03,1.03)`;
  };
  const onLeave = () => {
    if (ref.current)
      ref.current.style.transform =
        "perspective(800px) rotateX(0deg) rotateY(0deg) scale3d(1,1,1)";
  };
  return (
    <div
      ref={ref}
      style={{
        transition: "transform 0.15s ease",
        willChange: "transform",
        ...style,
      }}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
    >
      {children}
    </div>
  );
}

// ── Animated Counter
function AnimCounter({
  target,
  suffix = "",
  duration = 1800,
}: AnimCounterProps) {
  const [val, setVal] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          let v = 0;
          const step = Math.ceil(target / (duration / 16));
          const t = setInterval(() => {
            v += step;
            if (v >= target) {
              setVal(target);
              clearInterval(t);
            } else setVal(v);
          }, 16);
          obs.disconnect();
        }
      },
      { threshold: 0.5 },
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [target, duration]);
  return (
    <span ref={ref}>
      {val}
      {suffix}
    </span>
  );
}

// ── Brain Hero
function BrainHero({ speaking = false }: { speaking?: boolean }) {
  const orbitColors = speaking
    ? ["#ff6b6b", "#ffd700", "#00f5ff", "#7C3AED"]
    : ["#00f5ff", "#00f5ff", "#7C3AED", "#7C3AED"];
  const nucleusGlow = speaking
    ? "0 0 50px rgba(255,107,107,.5),inset 0 0 35px rgba(255,215,0,.3)"
    : "0 0 50px rgba(0,245,255,.35),inset 0 0 35px rgba(124,58,237,.3)";
  const nucleusBg = speaking
    ? "radial-gradient(circle,rgba(255,107,107,.5) 0%,rgba(255,215,0,.15) 70%,transparent 100%)"
    : "radial-gradient(circle,rgba(124,58,237,.45) 0%,rgba(0,245,255,.12) 70%,transparent 100%)";
  const ringColor = speaking ? "rgba(255,215,0," : "rgba(0,245,255,";
  return (
    <div
      style={{
        position: "relative",
        width: 300,
        height: 300,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {[1, 2, 3].map((i) => (
        <div
          key={i}
          style={{
            position: "absolute",
            width: 200 + i * 46,
            height: 200 + i * 46,
            borderRadius: "50%",
            border: `1px solid ${ringColor}${0.14 - i * 0.04})`,
            transition: "border-color .4s",
            animation: `spin ${8 + i * 4}s linear infinite ${i % 2 === 0 ? "reverse" : ""}`,
          }}
        />
      ))}
      <div
        style={{
          width: 160,
          height: 160,
          borderRadius: "50%",
          background: nucleusBg,
          boxShadow: nucleusGlow,
          transition: "background .4s,box-shadow .4s",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          animation: "float 3s ease-in-out infinite",
          border: `1px solid ${speaking ? "rgba(255,215,0,.4)" : "rgba(0,245,255,.3)"}`,
        }}
      >
        <svg width="90" height="90" viewBox="0 0 100 100" fill="none">
          <path
            d="M50 15C35 15 25 26 25 38C25 44 27 50 31 54C28 57 26 62 26 68C26 78 34 85 44 85C46 85 48 85 50 84V85H52C54 85 56 85 57 84C67 84 74 77 74 68C74 62 72 57 69 54C73 50 75 44 75 38C75 26 65 15 50 15Z"
            fill="url(#bgr)"
            opacity=".9"
          />
          <path
            d="M38 40C38 40 42 36 50 36C58 36 62 40 62 40"
            stroke={speaking ? "rgba(255,215,0,.9)" : "rgba(0,245,255,.8)"}
            strokeWidth="2"
            strokeLinecap="round"
          />
          <path
            d="M35 52C35 52 40 48 50 48C60 48 65 52 65 52"
            stroke={speaking ? "rgba(255,107,107,.7)" : "rgba(0,245,255,.6)"}
            strokeWidth="1.5"
            strokeLinecap="round"
          />
          <circle
            cx="50"
            cy="60"
            r="4"
            fill={speaking ? "rgba(255,215,0,.9)" : "rgba(0,245,255,.9)"}
          />
          {[0, 60, 120, 180, 240, 300].map((a, i) => (
            <circle
              key={i}
              cx={50 + 20 * Math.cos((a * Math.PI) / 180)}
              cy={60 + 20 * Math.sin((a * Math.PI) / 180)}
              r="2.5"
              fill={
                speaking
                  ? i % 2 === 0
                    ? "rgba(255,107,107,.9)"
                    : "rgba(255,215,0,.9)"
                  : "rgba(167,139,250,.9)"
              }
            />
          ))}
          <defs>
            <linearGradient id="bgr" x1="25" y1="15" x2="75" y2="85">
              <stop
                offset="0%"
                stopColor={speaking ? "#ff6b6b" : "#7c3aed"}
                stopOpacity=".8"
              />
              <stop
                offset="100%"
                stopColor={speaking ? "#ffd700" : "#00f5ff"}
                stopOpacity=".6"
              />
            </linearGradient>
          </defs>
        </svg>
      </div>
      {[0, 90, 180, 270].map((deg, i) => (
        <div
          key={i}
          style={{
            position: "absolute",
            width: 7,
            height: 7,
            borderRadius: "50%",
            background: orbitColors[i],
            boxShadow: `0 0 ${speaking ? 12 : 8}px ${orbitColors[i]}`,
            transition: "background .4s,box-shadow .4s",
            transform: `rotate(${deg}deg) translateX(118px)`,
            animation: "orbit 6s linear infinite",
            animationDelay: `${-i * 1.5}s`,
          }}
        />
      ))}
    </div>
  );
}

// ── Stat Badge
function StatBadge({
  number,
  label,
  color = "#00f5ff",
}: {
  number: string;
  label: string;
  color?: string;
}) {
  const num = parseInt(number);
  const suffix = number.replace(/\d/g, "");
  return (
    <div
      style={{
        background: "rgba(255,255,255,.05)",
        border: `1px solid ${color}40`,
        borderRadius: 14,
        padding: "16px 20px",
        backdropFilter: "blur(12px)",
        textAlign: "center",
        minWidth: 128,
      }}
    >
      <div
        style={{
          fontSize: "1.8rem",
          fontWeight: 800,
          fontFamily: "'Orbitron',monospace",
          color,
          textShadow: `0 0 18px ${color}`,
        }}
      >
        <AnimCounter target={num} suffix={suffix} />
      </div>
      <div
        style={{
          fontSize: ".72rem",
          color: "rgba(255,255,255,.55)",
          marginTop: 4,
          letterSpacing: ".08em",
        }}
      >
        {label}
      </div>
    </div>
  );
}

// ── Section Card
function SectionCard({
  side,
  title,
  description,
  stats,
  categories,
  ctaLabel,
  ctaColor,
  icon,
  onCta,
}: SectionCardProps) {
  const isLeft = side === "left";
  return (
    <TiltCard style={{ flex: "1 1 420px", minWidth: 0 }}>
      <div
        style={{
          background: "rgba(255,255,255,.04)",
          border: `1px solid ${ctaColor}28`,
          borderRadius: 24,
          padding: "36px 32px",
          height: "100%",
          backdropFilter: "blur(20px)",
          boxShadow: `0 8px 50px ${ctaColor}12,inset 0 1px 0 rgba(255,255,255,.07)`,
          position: "relative",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          gap: 20,
        }}
      >
        <div
          style={{
            position: "absolute",
            top: -70,
            [isLeft ? "left" : "right"]: -70,
            width: 180,
            height: 180,
            borderRadius: "50%",
            background: `radial-gradient(circle,${ctaColor}18 0%,transparent 70%)`,
            pointerEvents: "none",
          }}
        />
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: 14,
              background: `linear-gradient(135deg,${ctaColor}28,${ctaColor}0a)`,
              border: `1px solid ${ctaColor}38`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "1.8rem",
              flexShrink: 0,
            }}
          >
            {icon}
          </div>
          <div>
            <div
              style={{
                fontSize: ".68rem",
                letterSpacing: ".2em",
                color: ctaColor,
                textTransform: "uppercase",
                marginBottom: 6,
                fontFamily: "'Orbitron',monospace",
              }}
            >
              {isLeft ? "Banking" : "Insurance"} · AI Platform
            </div>
            <h2
              style={{
                fontSize: "1.6rem",
                fontWeight: 800,
                color: "#fff",
                fontFamily: "'Orbitron',monospace",
                margin: 0,
              }}
            >
              {title}
            </h2>
          </div>
        </div>
        <p
          style={{
            color: "rgba(255,255,255,.6)",
            fontSize: ".88rem",
            lineHeight: 1.72,
            margin: 0,
          }}
        >
          {description}
        </p>
        {stats && (
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            {stats.map((s, i) => (
              <StatBadge
                key={i}
                number={s.number}
                label={s.label}
                color={ctaColor}
              />
            ))}
          </div>
        )}
        {categories && (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {categories.map((cat, i) => (
              <div
                key={i}
                style={{
                  background: "rgba(255,255,255,.03)",
                  border: "1px solid rgba(255,255,255,.07)",
                  borderRadius: 12,
                  padding: "12px 16px",
                }}
              >
                <div
                  style={{
                    color: ctaColor,
                    fontWeight: 700,
                    fontSize: ".82rem",
                    fontFamily: "'Orbitron',monospace",
                    marginBottom: 7,
                  }}
                >
                  {cat.name}
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  {cat.stats.map((s, j) => (
                    <div
                      key={j}
                      style={{
                        background: `${ctaColor}12`,
                        borderRadius: 8,
                        padding: "5px 10px",
                        fontSize: ".75rem",
                        color: "rgba(255,255,255,.82)",
                        border: `1px solid ${ctaColor}22`,
                      }}
                    >
                      <span style={{ color: ctaColor, fontWeight: 700 }}>
                        {s.number}
                      </span>{" "}
                      {s.label}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
        <button
          onClick={onCta}
          style={{
            marginTop: "auto",
            padding: "12px 28px",
            borderRadius: 12,
            border: `1px solid ${ctaColor}55`,
            background: `linear-gradient(135deg,${ctaColor}18,${ctaColor}05)`,
            color: ctaColor,
            fontFamily: "'Orbitron',monospace",
            fontWeight: 700,
            fontSize: ".82rem",
            letterSpacing: ".1em",
            cursor: "pointer",
            transition: "all .3s",
            alignSelf: "flex-start",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = `linear-gradient(135deg,${ctaColor}38,${ctaColor}12)`;
            e.currentTarget.style.transform = "translateY(-2px)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = `linear-gradient(135deg,${ctaColor}18,${ctaColor}05)`;
            e.currentTarget.style.transform = "translateY(0)";
          }}
        >
          {ctaLabel} →
        </button>
      </div>
    </TiltCard>
  );
}

// ── Navbar
function Navbar({ activeSection, setActiveSection }: NavbarProps) {
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", h);
    return () => window.removeEventListener("scroll", h);
  }, []);
  return (
    <nav
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        padding: "0 clamp(1rem,5vw,2.5rem)",
        height: "clamp(56px, 8vw, 68px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        background: scrolled ? "rgba(2,4,20,.92)" : "transparent",
        backdropFilter: scrolled ? "blur(20px)" : "none",
        borderBottom: scrolled ? "1px solid rgba(0,245,255,.1)" : "none",
        transition: "all .3s",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "clamp(0.6rem, 2vw, 0.625rem)",
        }}
      >
        <div
          style={{
            width: "clamp(28px, 4vw, 34px)",
            height: "clamp(28px, 4vw, 34px)",
            borderRadius: 9,
            background: "linear-gradient(135deg,#00f5ff,#7c3aed)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "clamp(0.75rem, 2vw, 0.95rem)",
            flexShrink: 0,
          }}
        >
          ⬡
        </div>
        <span
          style={{
            fontFamily: "'Orbitron',monospace",
            fontWeight: 800,
            fontSize: "clamp(0.8rem, 2vw, 1rem)",
            whiteSpace: "nowrap",
          }}
        >
          <span style={{ color: "#FFFFFF" }}>OXY</span>
          <span style={{ color: "#00D4FF" }}>BFS</span>
          <span style={{ color: "#7C3AED" }}>AI</span>
        </span>
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "clamp(1.5rem, 3vw, 1.75rem)",
        }}
      >
        {["Home"].map((item) => (
          <a
            key={item}
            href="http://www.askoxy.ai"
            style={{
              color: "rgba(255,255,255,.65)",
              textDecoration: "none",
              fontSize: "clamp(0.65rem, 1.5vw, 0.8rem)",
              letterSpacing: ".08em",
              fontFamily: "'Orbitron',monospace",
              fontWeight: 500,
              transition: "color .2s",
              whiteSpace: "nowrap",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.color = "#00f5ff")}
            onMouseLeave={(e) =>
              (e.currentTarget.style.color = "rgba(255,255,255,.65)")
            }
          >
            {item}
          </a>
        ))}
      </div>
      <div
        style={{
          display: "flex",
          gap: "clamp(0.5rem, 1vw, 0.625rem)",
          flexShrink: 0,
          flexWrap: "nowrap",
        }}
      >
        {(
          [
            { label: "Banking", color: "#00f5ff" },
            { label: "Insurance", color: "#7C3AED" },
          ] as { label: string; color: string }[]
        ).map((btn) => (
          <button
            key={btn.label}
            onClick={() => {
              const section = btn.label.toLowerCase();
              setActiveSection(section);

              if (section === "banking") {
                handleNavigation(navigate, "/finvibe-code-builder");
              } else if (section === "insurance") {
                handleNavigation(navigate, "/insurvibe-code-builder");
              }
            }}
            style={{
              padding: "clamp(0.4rem, 1vw, 0.45rem) clamp(1rem, 2vw, 1.125rem)",
              borderRadius: 8,
              border: `1px solid ${btn.color}48`,
              cursor: "pointer",
              background:
                activeSection === btn.label.toLowerCase()
                  ? `linear-gradient(135deg,${btn.color}28,${btn.color}0a)`
                  : "transparent",
              color: btn.color,
              fontFamily: "'Orbitron',monospace",
              fontWeight: 700,
              fontSize: "clamp(0.6rem, 1.2vw, 0.72rem)",
              transition: "all .3s",
              whiteSpace: "nowrap",
              minWidth: "auto",
            }}
          >
            {btn.label}
          </button>
        ))}
      </div>
    </nav>
  );
}

// ══════════════════════════════════════════════
// ROADMAP — Clean dark design with ROADMAP_STEPS
// ══════════════════════════════════════════════

function StepCard({ s, delay = 0 }: { s: RoadmapStep; delay?: number }) {
  const [hov, setHov] = useState(false);
  return (
    <div style={{ flex: 1, minWidth: 0, animationDelay: `${delay}s` }}>
      <div
        onMouseEnter={() => setHov(true)}
        onMouseLeave={() => setHov(false)}
        style={{
          background: hov ? `${s.color}12` : "rgba(255,255,255,.03)",
          border: `1px solid ${hov ? s.color + "60" : s.color + "22"}`,
          borderRadius: 14,
          padding: "16px 14px",
          transition: "all .22s",
          cursor: "default",
          position: "relative",
          overflow: "hidden",
          height: "100%",
          boxShadow: hov ? `0 6px 24px ${s.color}20` : "none",
        }}
      >
        {/* Step number badge */}
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 26,
            height: 26,
            borderRadius: 8,
            background: `${s.color}20`,
            border: `1px solid ${s.color}40`,
            fontSize: "0.7rem",
            fontWeight: 700,
            color: s.color,
            marginBottom: 10,
            fontFamily: "sans-serif",
          }}
        >
          {s.num}
        </div>
        {/* Icon */}
        <div style={{ fontSize: "1.4rem", marginBottom: 8, lineHeight: 1 }}>
          {s.icon}
        </div>
        {/* Tag */}
        <span
          style={{
            fontSize: "0.65rem",
            fontWeight: 600,
            letterSpacing: ".04em",
            textTransform: "uppercase",
            padding: "2px 8px",
            borderRadius: 4,
            background: `${s.color}18`,
            color: s.color,
            border: `1px solid ${s.color}30`,
            display: "inline-block",
            marginBottom: 8,
            fontFamily: "sans-serif",
          }}
        >
          {s.tag}
        </span>
        {/* Title */}
        <div
          style={{
            fontFamily: "sans-serif",
            fontWeight: 700,
            fontSize: "0.85rem",
            color: hov ? "#fff" : "rgba(255,255,255,.9)",
            marginBottom: 6,
            lineHeight: 1.3,
          }}
        >
          {s.title}
        </div>
        {/* Desc */}
        <p
          style={{
            fontSize: "0.75rem",
            color: "rgba(255,255,255,.55)",
            lineHeight: 1.6,
            margin: 0,
            fontFamily: "sans-serif",
          }}
        >
          {s.desc}
        </p>
        {/* Bottom bar */}
        <div
          style={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: 2,
            background: `linear-gradient(90deg,transparent,${s.color}${hov ? "99" : "30"},transparent)`,
            transition: "all .25s",
          }}
        />
      </div>
    </div>
  );
}

function ChevronArrow({ color }: { color: string }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        width: 24,
        padding: "0 1px",
      }}
    >
      <svg width="18" height="12" viewBox="0 0 18 12" fill="none">
        <path
          d="M1 6H14M14 6L9 1M14 6L9 11"
          stroke={color}
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
          opacity=".65"
        />
      </svg>
    </div>
  );
}

function SnakeBend({
  fromColor,
  toColor,
}: {
  fromColor: string;
  toColor: string;
}) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "flex-end",
        paddingRight: 10,
        height: 36,
        margin: "2px 0",
      }}
    >
      <svg
        width="52"
        height="36"
        viewBox="0 0 52 36"
        fill="none"
        overflow="visible"
      >
        <defs>
          <linearGradient id="snk" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={fromColor} stopOpacity=".5" />
            <stop offset="100%" stopColor={toColor} stopOpacity=".5" />
          </linearGradient>
        </defs>
        <path
          d="M 5 3 Q 47 3 47 18 Q 47 33 5 33"
          stroke="url(#snk)"
          strokeWidth="1.6"
          fill="none"
          strokeLinecap="round"
        />
        <path
          d="M 10 27 L 4 33 L 10 39"
          stroke={toColor}
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
          opacity=".6"
        />
      </svg>
    </div>
  );
}

// ── Roadmap Section (inline, no modal)
function RoadmapSection({ country }: { country: Country }) {
  const isMobile = useIsMobile(860);
  const steps = ROADMAP_STEPS.map((s) =>
    s.num === "04" ? { ...s, desc: COMPLIANCE_DESC[country] } : s,
  );
  const row1 = steps.slice(0, 6);
  const row2 = steps.slice(6);
  const renderTrack = (items: RoadmapStep[]) => (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: isMobile ? "1fr" : "repeat(6, minmax(0, 1fr))",
        gap: isMobile ? 12 : 0,
        alignItems: "stretch",
        marginBottom: isMobile ? 18 : 0,
      }}
    >
      {items.map((s, i) => (
        <div
          key={s.num}
          style={{
            display: "flex",
            alignItems: "stretch",
            flexDirection: isMobile ? "column" : "row",
            minWidth: 0,
          }}
        >
          <StepCard s={s} delay={i * 0.05} />
          {!isMobile && i < items.length - 1 && (
            <ChevronArrow color={items[i + 1].color} />
          )}
        </div>
      ))}
    </div>
  );
  return (
    <section
      style={{
        position: "relative",
        zIndex: 2,
        padding:
          "clamp(4rem, 10vw, 5rem) clamp(1rem, 5vw, 2.5rem) clamp(3rem, 8vw, 3.75rem)",
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          zIndex: -1,
          background:
            "radial-gradient(ellipse 70% 50% at 50% 50%,rgba(59,111,255,.06) 0%,transparent 70%)",
        }}
      />
      <div style={{ maxWidth: 1080, margin: "0 auto" }}>
        {/* Heading */}
        <div
          style={{
            textAlign: "center",
            marginBottom: "clamp(2rem, 5vw, 2.5rem)",
          }}
        >
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              background: "rgba(0,245,255,.07)",
              border: "1px solid rgba(0,245,255,.18)",
              borderRadius: 100,
              padding: "3px 14px",
              marginBottom: "clamp(0.75rem, 2vw, 0.75rem)",
            }}
          >
            <span
              style={{
                width: 5,
                height: 5,
                borderRadius: "50%",
                background: "#00f5ff",
                display: "inline-block",
                animation: "rdmPulse 1.6s ease-in-out infinite",
              }}
            />
            <span
              style={{
                fontSize: "clamp(0.55rem, 1vw, 0.7rem)",
                fontWeight: 600,
                letterSpacing: ".06em",
                textTransform: "uppercase",
                color: "#00f5ff",
                fontFamily: "sans-serif",
              }}
            >
              AI Generation Pipeline
            </span>
          </div>
          <h2
            style={{
              fontFamily: "'Orbitron',monospace",
              fontSize: "clamp(1.3rem,3vw,2rem)",
              fontWeight: 800,
              marginBottom: "clamp(0.5rem, 1.5vw, 0.625rem)",
            }}
          >
            How <span style={{ color: "#FFFFFF" }}>OXY</span>
            <span style={{ color: "#00D4FF" }}>BFS</span>
            <span style={{ color: "#7C3AED" }}>AI</span> Works
          </h2>
          <p
            style={{
              color: "rgba(255,255,255,.38)",
              fontSize: "clamp(0.75rem, 1.5vw, 0.85rem)",
              maxWidth: 460,
              margin: "0 auto",
              lineHeight: 1.7,
            }}
          >
            12 sequential AI stages · Each feeds directly into the next · Done
            in under 60 seconds
          </p>
        </div>

        {/* Track A */}
        <div
          style={{
            fontSize: "0.7rem",
            letterSpacing: ".12em",
            textTransform: "uppercase",
            color: "rgba(255,255,255,.35)",
            fontFamily: "sans-serif",
            fontWeight: 600,
            marginBottom: 12,
          }}
        >
          ◆ Track A — Foundation &amp; Architecture (Steps 01–06)
        </div>
        {renderTrack(row1)}

        {!isMobile && (
          <SnakeBend
            fromColor={row1[row1.length - 1].color}
            toColor={row2[0].color}
          />
        )}

        {/* Track B */}
        <div
          style={{
            fontSize: "0.7rem",
            letterSpacing: ".12em",
            textTransform: "uppercase",
            color: "rgba(255,255,255,.35)",
            fontFamily: "sans-serif",
            fontWeight: 600,
            marginBottom: 12,
          }}
        >
          ◆ Track B — Generation &amp; Validation (Steps 07–12)
        </div>
        {renderTrack(row2)}

        {/* Stats strip */}
        <div
          style={{
            padding: "12px 16px",
            background: "rgba(255,255,255,.025)",
            border: "1px solid rgba(255,255,255,.06)",
            borderRadius: 12,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
            <span style={{ fontSize: 16 }}>⚡</span>
            <div>
              <p
                style={{
                  fontSize: "0.82rem",
                  fontWeight: 600,
                  color: "rgba(255,255,255,.85)",
                  margin: "0 0 2px",
                  fontFamily: "sans-serif",
                }}
              >
                All 12 stages complete in under 60 seconds
              </p>
              <p
                style={{
                  fontSize: "0.75rem",
                  color: "rgba(255,255,255,.45)",
                  margin: 0,
                  fontFamily: "sans-serif",
                }}
              >
                Every stage streams live — watch your app materialise token by
                token.
              </p>
            </div>
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {(
              [
                ["Analysis", "#3B6FFF"],
                ["Architecture", "#7C3AED"],
                ["Code Gen", "#DC2626"],
                ["Quality", "#fff"],
              ] as [string, string][]
            ).map(([t, c]) => (
              <span
                key={t}
                style={{
                  fontSize: "0.7rem",
                  fontWeight: 600,
                  letterSpacing: ".04em",
                  textTransform: "uppercase",
                  padding: "3px 10px",
                  borderRadius: 5,
                  background: `${c}15`,
                  color: c,
                  border: `1px solid ${c}30`,
                  fontFamily: "sans-serif",
                }}
              >
                {t}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Country selector data (same as Billing)
const FLAG_COMPONENTS_LANDING: Record<Country, () => JSX.Element> = {
  india: () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 900 600"
      style={{ width: 40, height: 27, borderRadius: 4, display: "block" }}
    >
      <rect width="900" height="200" fill="#FF9933" />
      <rect y="200" width="900" height="200" fill="#fff" />
      <rect y="400" width="900" height="200" fill="#138808" />
      <circle
        cx="450"
        cy="300"
        r="70"
        fill="none"
        stroke="#000080"
        strokeWidth="7"
      />
      {Array.from({ length: 24 }, (_, i) => {
        const a = ((i * 15 - 90) * Math.PI) / 180;
        return (
          <line
            key={i}
            x1={450 + 55 * Math.cos(a)}
            y1={300 + 55 * Math.sin(a)}
            x2={450 + 70 * Math.cos(a)}
            y2={300 + 70 * Math.sin(a)}
            stroke="#000080"
            strokeWidth="3"
          />
        );
      })}
      <circle cx="450" cy="300" r="10" fill="#000080" />
    </svg>
  ),
  uae: () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 900 600"
      style={{ width: 40, height: 27, borderRadius: 4, display: "block" }}
    >
      <rect width="900" height="600" fill="#fff" />
      <rect width="900" height="200" fill="#00732F" />
      <rect y="400" width="900" height="200" fill="#000" />
      <rect width="225" height="600" fill="#FF0000" />
    </svg>
  ),
  saudi: () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 900 600"
      style={{ width: 40, height: 27, borderRadius: 4, display: "block" }}
    >
      <rect width="900" height="600" fill="#006C35" />
      <text
        x="450"
        y="280"
        textAnchor="middle"
        fill="#fff"
        fontSize="120"
        fontFamily="serif"
      >
        &#x644;&#x627; &#x625;&#x644;&#x647; &#x625;&#x644;&#x627;
        &#x627;&#x644;&#x644;&#x647;
      </text>
      <rect x="300" y="360" width="300" height="12" fill="#fff" />
      <text
        x="450"
        y="440"
        textAnchor="middle"
        fill="#fff"
        fontSize="80"
        fontFamily="serif"
      >
        &#x2694;
      </text>
    </svg>
  ),
};

const LANDING_COUNTRIES: { id: Country; label: string; sub: string }[] = [
  { id: "india", label: "India", sub: "RBI · IRDAI" },
  { id: "uae", label: "UAE", sub: "CBUAE · UAE IA" },
  { id: "saudi", label: "Saudi Arabia", sub: "SAMA · Vision 2030" },
];

const FINVIBE_FEATURES: Record<Country, string[]> = {
  india: [
    "Covers 15,000+ RBI Regulated Entity Requirements",
    "Follows RBI Master Directions & Guidelines — 1,00,000+ Master Circulars",
    "Supports 11 Bank Types: Small Finance Banks, Urban Co-operative Banks, Payment Banks, Local Area Banks & more",
    "Complete Banking Application from a Single Prompt",
    "Frontend + Backend + Database Code — Ready to Deploy",
    "One-click Download of the Full Application Codebase",
  ],
  uae: [
    "Covers 10,000+ CBUAE Regulatory Requirements & Circulars",
    "Supports 49+ Licensed Banks: 23 National + 26 Foreign Bank Branches",
    "Covers 8 Bank Categories: Commercial, Islamic, Digital, Exchange Houses & more",
    "Compliant with UAE AML/CFT, Open Banking & DFSA Frameworks",
    "Frontend + Backend + Database Code — Ready to Deploy",
    "One-click Download of the Full Application Codebase",
  ],
  saudi: [
    "Covers 10,000+ SAMA Regulatory Requirements & Circulars",
    "Supports 26+ Licensed Banks: 12 Local Banks + 14 Foreign Bank Branches",
    "Covers 8 Bank Categories: Commercial, Islamic, Digital, Finance Companies & more",
    "Compliant with SAMA Open Banking & Vision 2030 Fintech Frameworks",
    "Frontend + Backend + Database Code — Ready to Deploy",
    "One-click Download of the Full Application Codebase",
  ],
};

const INSURVIBE_FEATURES: Record<Country, string[]> = {
  india: [
    "Addresses 2,800+ IRDAI Products & Policy Types",
    "Supports 62 Insurance Companies",
    "Covers 638 Brokerage Firms Nationwide",
    "Full Compliance with IRDAI Regulatory Framework",
    "Frontend + Backend + Database Code — Ready to Deploy",
    "Complete Insurance Application from a Single Prompt",
    "Covers Life, Health, Motor & General Insurance Domains",
  ],
  uae: [
    "Covers UAE Insurance Authority (IA) Regulatory Requirements",
    "Supports 60+ Licensed Insurance Companies in UAE",
    "Covers 900+ Insurance Products across all Lines of Business",
    "Compliant with UAE Takaful (Islamic Insurance) Frameworks",
    "Covers Life & Takaful, Health, Motor & General Insurance",
    "Frontend + Backend + Database Code — Ready to Deploy",
    "Complete Insurance Application from a Single Prompt",
  ],
  saudi: [
    "Covers SAMA Insurance Supervision Regulatory Requirements",
    "Supports 30+ Licensed Cooperative Insurance Companies in KSA",
    "Covers 900+ Insurance Products across all Lines of Business",
    "Compliant with Saudi Vision 2030 Insurance Sector Reforms",
    "Covers Cooperative Life, Health, Motor & General Takaful",
    "Frontend + Backend + Database Code — Ready to Deploy",
    "Complete Insurance Application from a Single Prompt",
  ],
};

const FINVIBE_DESC: Record<Country, string> = {
  india:
    "Build a complete, RBI-compliant banking application with a single prompt.",
  uae: "Build a complete, CBUAE-compliant banking application with a single prompt.",
  saudi:
    "Build a complete, SAMA-compliant banking application with a single prompt.",
};

const INSURVIBE_DESC: Record<Country, string> = {
  india:
    "Build complete IRDAI-compliant insurance platforms — life, health, motor & general — from one prompt.",
  uae: "Build complete UAE Insurance Authority-compliant platforms — takaful, health, motor & general — from one prompt.",
  saudi:
    "Build complete SAMA-compliant cooperative insurance platforms — takaful, health, motor & general — from one prompt.",
};

const FINVIBE_TAGS: Record<Country, string[]> = {
  india: [
    "RBI Master Directions",
    "15,000+ Requirements",
    "11 Bank Types",
    "1,00,000+ Circulars",
  ],
  uae: [
    "CBUAE Compliant",
    "10,000+ Requirements",
    "49+ Banks",
    "8 Bank Categories",
  ],
  saudi: [
    "SAMA Compliant",
    "10,000+ Requirements",
    "26+ Banks",
    "8 Bank Categories",
  ],
};

const INSURVIBE_TAGS: Record<Country, string[]> = {
  india: [
    "IRDAI Compliant",
    "2,800+ Products",
    "62 Companies",
    "638 Brokerages",
  ],
  uae: ["UAE IA Compliant", "60+ Companies", "900+ Products", "Takaful Ready"],
  saudi: ["SAMA Compliant", "30+ Companies", "900+ Products", "Vision 2030"],
};

// ── Country-aware compliance desc for roadmap step 04
const COMPLIANCE_DESC: Record<Country, string> = {
  india:
    "Applies RBI Master Directions, KYC/AML, IRDAI, GDPR, PCI-DSS rules automatically.",
  uae: "Applies CBUAE, UAE IA, AML/CFT, DFSA, Open Banking & PCI-DSS rules automatically.",
  saudi:
    "Applies SAMA, Vision 2030, KYC/AML, Takaful & PCI-DSS compliance rules automatically.",
};

const COUNTRY_BANKING_STATS: Record<
  Country,
  { number: string; label: string }[]
> = {
  india: [
    { number: "15000+", label: "RBI Regulated Entity" },
    { number: "11+", label: "Regulated Banks" },
  ],
  uae: [
    { number: "60+", label: "CBUAE Licensed Banks" },
    { number: "8+", label: "Bank Categories" },
  ],
  saudi: [
    { number: "30+", label: "SAMA Licensed Banks" },
    { number: "10+", label: "Bank Categories" },
  ],
};

const COUNTRY_INSURANCE_CATS: Record<
  Country,
  { name: string; stats: { number: string; label: string }[] }[]
> = {
  india: [
    {
      name: "🛡️ Life Insurance",
      stats: [
        { number: "28+", label: "Companies" },
        { number: "2800+", label: "Products" },
      ],
    },
    {
      name: "🔒 General Insurance",
      stats: [
        { number: "30+", label: "Providers" },
        { number: "1200+", label: "Products" },
      ],
    },
  ],
  uae: [
    {
      name: "🛡️ Life & Takaful",
      stats: [
        { number: "25+", label: "Companies" },
        { number: "500+", label: "Products" },
      ],
    },
    {
      name: "🔒 General Insurance",
      stats: [
        { number: "35+", label: "Providers" },
        { number: "400+", label: "Products" },
      ],
    },
  ],
  saudi: [
    {
      name: "🛡️ Cooperative Insurance",
      stats: [
        { number: "30+", label: "Companies" },
        { number: "600+", label: "Products" },
      ],
    },
    {
      name: "🔒 General Takaful",
      stats: [
        { number: "20+", label: "Providers" },
        { number: "300+", label: "Products" },
      ],
    },
  ],
};

const COUNTRY_STATS: Record<
  Country,
  { num: number; suffix: string; label: string; color: string }[]
> = {
  india: [
    { num: 253, suffix: "+", label: "Master Directions", color: "#00f5ff" },
    { num: 51, suffix: "+", label: "Banking Use Cases", color: "#00f5ff" },
    { num: 28, suffix: "+", label: "Insurance Companies", color: "#7C3AED" },
    { num: 2800, suffix: "+", label: "Insurance Products", color: "#7C3AED" },
    { num: 99, suffix: "%", label: "AI Accuracy", color: "#22d3ee" },
  ],
  uae: [
    { num: 60, suffix: "+", label: "CBUAE Licensed Banks", color: "#00f5ff" },
    { num: 40, suffix: "+", label: "Banking Use Cases", color: "#00f5ff" },
    { num: 60, suffix: "+", label: "Insurance Companies", color: "#7C3AED" },
    { num: 900, suffix: "+", label: "Insurance Products", color: "#7C3AED" },
    { num: 99, suffix: "%", label: "AI Accuracy", color: "#22d3ee" },
  ],
  saudi: [
    { num: 30, suffix: "+", label: "SAMA Licensed Banks", color: "#00f5ff" },
    { num: 35, suffix: "+", label: "Banking Use Cases", color: "#00f5ff" },
    { num: 30, suffix: "+", label: "Insurance Companies", color: "#7C3AED" },
    { num: 900, suffix: "+", label: "Insurance Products", color: "#7C3AED" },
    { num: 99, suffix: "%", label: "AI Accuracy", color: "#22d3ee" },
  ],
};

export default function Finvibe3DLanding({ country }: { country?: Country }) {
  const resolvedCountry: Country =
    country ??
    ((sessionStorage.getItem("finvibe_country") as Country) || "india");
  const [selectedCountry, setSelectedCountry] =
    useState<Country>(resolvedCountry);
  const [activeSection, setActiveSection] = useState("banking");
  useForceBodyDark();
  const speaking = useGreetingSpeech();
  const navigate = useNavigate();
  const bankingStats = COUNTRY_BANKING_STATS[selectedCountry];
  const insuranceCats = COUNTRY_INSURANCE_CATS[selectedCountry];
  const pageStats = COUNTRY_STATS[selectedCountry];

  const handleCountryChange = (c: Country) => {
    setSelectedCountry(c);
    sessionStorage.setItem("finvibe_country", c);
  };

  return (
    <div
      style={{
        background: "#020414",
        minHeight: "100vh",
        color: "#fff",
        fontFamily: "'Sora',sans-serif",
        overflowX: "hidden",
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@400;500;700;800;900&family=Sora:wght@300;400;500;600&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        html { scroll-behavior: smooth; background: #020414; }
        body { background: #020414 !important; }
        #root { background: #020414; }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: #020414; }
        ::-webkit-scrollbar-thumb { background: linear-gradient(#00f5ff,#7c3aed); border-radius: 2px; }
        @keyframes float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-16px)} }
        @keyframes spin { to{transform:rotate(360deg)} }
        @keyframes orbit { from{transform:rotate(0deg) translateX(118px)} to{transform:rotate(360deg) translateX(118px)} }
        @keyframes pulseGlow { 0%,100%{opacity:1} 50%{opacity:.3} }
        @keyframes fadeInUp { from{opacity:0;transform:translateY(28px)} to{opacity:1;transform:translateY(0)} }
        @keyframes shimmer { 0%{background-position:-200% center} 100%{background-position:200% center} }
        @keyframes rdmPulse { 0%,100%{opacity:1} 50%{opacity:.25} }
        @keyframes voiceColor {
          0%   { background-position: 0% center }
          25%  { background-position: 50% center }
          50%  { background-position: 100% center }
          75%  { background-position: 50% center }
          100% { background-position: 0% center }
        }
        button { -webkit-tap-highlight-color: transparent; }
        @media (max-width: 768px) {
          canvas { opacity: .55; }
          nav { gap: 10px; }
        }
      `}</style>

      <ParticleCanvas />
      {/* scanline */}
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 1,
          pointerEvents: "none",
          backgroundImage:
            "repeating-linear-gradient(0deg,transparent,transparent 2px,rgba(0,0,0,.022) 2px,rgba(0,0,0,.022) 4px)",
        }}
      />

      <Navbar
        activeSection={activeSection}
        setActiveSection={setActiveSection}
      />

      <section
        style={{
          position: "relative",
          zIndex: 2,
          minHeight: "60vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding:
            "clamp(5rem, 12vw, 6rem) clamp(1rem, 5vw, 2.5rem) clamp(3rem, 8vw, 4rem)",
          textAlign: "center",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            zIndex: -1,
            background:
              "radial-gradient(ellipse 80% 60% at 50% 0%,rgba(124,58,237,.14) 0%,transparent 70%),radial-gradient(ellipse 60% 40% at 20% 100%,rgba(0,245,255,.07) 0%,transparent 60%)",
          }}
        />

        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 10,
            background:
              "linear-gradient(135deg,rgba(0,245,255,.1),rgba(124,58,237,.08))",
            border: "1px solid rgba(0,245,255,.25)",
            borderRadius: 100,
            padding: "6px 18px",
            marginBottom: 24,
            animation: "fadeInUp .8s ease forwards",
            backdropFilter: "blur(12px)",
          }}
        >
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              background: "#00f5ff",
              animation: "pulseGlow 2s infinite",
            }}
          />
          <span
            style={{
              fontSize: "clamp(0.65rem, 1.2vw, 0.73rem)",
              letterSpacing: ".14em",
              color: "#00f5ff",
              fontFamily: "'Orbitron',monospace",
              fontWeight: 600,
            }}
          >
            ⚡ AI-POWERED FINTECH PLATFORM
          </span>
        </div>

        <h1
          style={{
            fontSize: "clamp(1.8rem,5vw,3.8rem)",
            fontFamily: "'Orbitron',monospace",
            fontWeight: 900,
            lineHeight: 1.05,
            marginBottom: 10,
            letterSpacing: "-.02em",
            animation: "fadeInUp .8s ease .12s both",
          }}
        >
          <span
            style={{
              color: "#FFFFFF",
              fontFamily: "'Orbitron',monospace",
              fontWeight: 900,
            }}
          >
            OXY
          </span>
          <span
            style={{
              color: "#00D4FF",
              fontFamily: "'Orbitron',monospace",
              fontWeight: 900,
            }}
          >
            BFS
          </span>
          <span
            style={{
              color: "#7C3AED",
              fontFamily: "'Orbitron',monospace",
              fontWeight: 900,
            }}
          >
            AI
          </span>
        </h1>

        <h2
          style={{
            fontSize: "clamp(1rem,2vw,1.5rem)",
            fontFamily: "'Orbitron',monospace",
            fontWeight: 700,
            background: "linear-gradient(90deg,#00f5ff,#7C3AED)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            marginBottom: 14,
            animation: "fadeInUp .8s ease .2s both",
            letterSpacing: ".02em",
          }}
        >
          Vibe Code
        </h2>

        <p
          style={{
            fontSize: "clamp(.8rem,1.5vw,.95rem)",
            color: "rgba(255,255,255,.55)",
            maxWidth: 500,
            lineHeight: 1.65,
            marginBottom: 28,
            animation: "fadeInUp .8s ease .3s both",
            fontWeight: 300,
          }}
        >
          Transforming{" "}
          <span style={{ color: "#00f5ff", fontWeight: 600 }}>Banking</span>{" "}
          &amp;{" "}
          <span style={{ color: "#7C3AED", fontWeight: 600 }}>Insurance</span>{" "}
          with Intelligent AI Systems
        </p>

        <div
          style={{
            animation: "fadeInUp .8s ease .44s both",
            transform: "scale(0.72)",
            transformOrigin: "center top",
            marginBottom: -30,
          }}
        >
          <BrainHero speaking={speaking} />
        </div>

        <div
          style={{
            marginTop: 20,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 6,
            animation: "fadeInUp .8s ease .58s both",
          }}
        >
          <div
            style={{
              fontSize: ".58rem",
              letterSpacing: ".2em",
              color: "rgba(255,255,255,.22)",
              fontFamily: "'Orbitron',monospace",
            }}
          >
            SCROLL TO EXPLORE
          </div>
          <div
            style={{
              width: 18,
              height: 28,
              border: "1px solid rgba(255,255,255,.16)",
              borderRadius: 9,
              display: "flex",
              justifyContent: "center",
              paddingTop: 4,
            }}
          >
            <div
              style={{
                width: 2,
                height: 5,
                borderRadius: 2,
                background: "#00f5ff",
                animation: "float 1.5s ease-in-out infinite",
              }}
            />
          </div>
        </div>
      </section>

      {/* ── SPLIT */}
      <section
        style={{
          position: "relative",
          zIndex: 2,
          padding:
            "clamp(3rem, 8vw, 4.5rem) clamp(1rem, 5vw, 2.5rem) clamp(4rem, 10vw, 5.625rem)",
        }}
      >
        <div
          style={{
            textAlign: "center",
            marginBottom: "clamp(2rem, 5vw, 3.125rem)",
          }}
        >
          {/* Country flag selector */}
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "clamp(0.75rem, 2vw, 0.75rem)",
              marginBottom: "clamp(1.5rem, 3vw, 1.75rem)",
              flexWrap: "wrap",
            }}
          >
            {LANDING_COUNTRIES.map((c) => {
              const FlagComp = FLAG_COMPONENTS_LANDING[c.id];
              const active = selectedCountry === c.id;
              return (
                <button
                  key={c.id}
                  onClick={() => handleCountryChange(c.id)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "clamp(0.5rem, 2vw, 0.625rem)",
                    padding:
                      "clamp(0.6rem, 1.5vw, 0.625rem) clamp(1rem, 2.5vw, 1.125rem)",
                    borderRadius: 14,
                    border: `2px solid ${active ? "rgba(0,245,255,.7)" : "rgba(255,255,255,.1)"}`,
                    background: active
                      ? "rgba(0,245,255,.1)"
                      : "rgba(255,255,255,.03)",
                    cursor: "pointer",
                    transition: "all .2s",
                    transform: active ? "scale(1.05)" : "scale(1)",
                    boxShadow: active
                      ? "0 4px 20px rgba(0,245,255,.2)"
                      : "none",
                  }}
                >
                  <div
                    style={{
                      boxShadow: active
                        ? "0 0 0 2px rgba(0,245,255,.5)"
                        : "0 0 0 1px rgba(255,255,255,.15)",
                      borderRadius: 4,
                    }}
                  >
                    <FlagComp />
                  </div>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      gap: 2,
                      minWidth: 0,
                    }}
                  >
                    <span
                      style={{
                        fontSize: "clamp(0.75rem, 2vw, 0.85rem)",
                        fontWeight: 700,
                        color: active ? "#67e8f9" : "#e2e8f0",
                        fontFamily: "sans-serif",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {c.label}
                    </span>
                    <span
                      style={{
                        fontSize: "clamp(0.5rem, 1vw, 0.6rem)",
                        fontWeight: 600,
                        letterSpacing: ".08em",
                        textTransform: "uppercase" as const,
                        color: active ? "#22d3ee" : "#64748b",
                        fontFamily: "sans-serif",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {c.sub}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
          <div
            style={{
              fontFamily: "'Orbitron',monospace",
              fontSize: "clamp(0.5rem, 1.5vw, 0.65rem)",
              letterSpacing: ".25em",
              color: "rgba(255,255,255,.22)",
              textTransform: "uppercase",
              marginBottom: "clamp(0.5rem, 1vw, 0.625rem)",
            }}
          >
            Core Intelligence Suite
          </div>
          <h2
            style={{
              fontFamily: "'Orbitron',monospace",
              fontSize: "clamp(1.2rem, 4vw, 2.2rem)",
              fontWeight: 800,
              background: "linear-gradient(90deg,#00f5ff,#7C3AED)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Dual Domain Mastery
          </h2>
        </div>
        <div
          style={{
            display: "flex",
            gap: "clamp(1rem, 3vw, 1.375rem)",
            alignItems: "stretch",
            maxWidth: 1080,
            margin: "0 auto",
            flexWrap: "wrap",
          }}
        >
          <SectionCard
            side="left"
            title="Banking Intelligence"
            description="Empowering financial institutions with scalable AI-driven solutions, automation frameworks, and deep analytics — from compliance intelligence to real-time transaction monitoring."
            stats={bankingStats}
            ctaLabel="Explore Banking"
            ctaColor="#00f5ff"
            icon="🏦"
            onCta={() => handleNavigation(navigate, "/finvibe-code-builder")}
          />
          <SectionCard
            side="right"
            title="Insurance Intelligence"
            description="Revolutionizing insurance ecosystems with intelligent product mapping, automation, and real-time insights — from policy lifecycle to claims intelligence and risk scoring."
            categories={insuranceCats}
            ctaLabel="Explore Insurance"
            ctaColor="#7C3AED"
            icon="🛡️"
            onCta={() => handleNavigation(navigate, "/insurvibe-code-builder")}
          />
        </div>
      </section>

      {/* ── STATS */}
      <section
        style={{
          position: "relative",
          zIndex: 2,
          padding: "clamp(3rem, 7vw, 3.75rem) clamp(1rem, 5vw, 2.5rem)",
          background:
            "linear-gradient(135deg,rgba(0,245,255,.035) 0%,rgba(124,58,237,.05) 100%)",
          borderTop: "1px solid rgba(0,245,255,.1)",
          borderBottom: "1px solid rgba(124,58,237,.1)",
          backdropFilter: "blur(8px)",
        }}
      >
        <div
          style={{
            maxWidth: 1050,
            margin: "0 auto",
            display: "flex",
            justifyContent: "space-around",
            flexWrap: "wrap",
            gap: "clamp(1rem, 3vw, 1.625rem)",
          }}
        >
          {pageStats.map((s, i) => (
            <div
              key={i}
              style={{
                textAlign: "center",
                padding: "clamp(0.5rem, 2vw, 0.75rem)",
              }}
            >
              <div
                style={{
                  fontSize: "clamp(1.8rem, 4vw, 2.8rem)",
                  fontWeight: 900,
                  fontFamily: "'Orbitron',monospace",
                  color: s.color,
                  textShadow: `0 0 28px ${s.color}35`,
                  marginBottom: 8,
                }}
              >
                <AnimCounter target={s.num} suffix={s.suffix} />
              </div>
              <div
                style={{
                  color: "rgba(255,255,255,.55)",
                  fontSize: "clamp(0.7rem, 1.2vw, 0.82rem)",
                  letterSpacing: ".06em",
                  fontWeight: 500,
                }}
              >
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── COUNTRY SELECTOR + PRODUCT CARDS */}
      <section
        style={{
          position: "relative",
          zIndex: 2,
          padding:
            "clamp(4rem, 10vw, 5rem) clamp(1rem, 5vw, 2.5rem) clamp(2rem, 5vw, 2.5rem)",
        }}
      >
        <div style={{ maxWidth: 1080, margin: "0 auto" }}>
          {/* FinVibe + InsurVibe product cards */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))",
              gap: "clamp(1rem, 3vw, 1.5rem)",
              marginBottom: "clamp(3rem, 8vw, 3.75rem)",
            }}
          >
            {/* FinVibe */}
            <div
              style={{
                background:
                  "linear-gradient(135deg,rgba(0,245,255,.09),rgba(37,99,235,.06))",
                border: "1px solid rgba(0,245,255,.25)",
                borderRadius: 20,
                padding: "clamp(1.75rem, 5vw, 2.25rem)",
                display: "flex",
                flexDirection: "column",
                position: "relative",
                overflow: "hidden",
                transition: "all .35s ease",
                boxShadow: "0 0 30px rgba(0,245,255,.08)",
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.borderColor =
                  "rgba(0,245,255,.4)";
                (e.currentTarget as HTMLElement).style.boxShadow =
                  "0 0 40px rgba(0,245,255,.16)";
                (e.currentTarget as HTMLElement).style.transform =
                  "translateY(-4px)";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.borderColor =
                  "rgba(0,245,255,.25)";
                (e.currentTarget as HTMLElement).style.boxShadow =
                  "0 0 30px rgba(0,245,255,.08)";
                (e.currentTarget as HTMLElement).style.transform =
                  "translateY(0)";
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: -40,
                  right: -40,
                  width: 160,
                  height: 160,
                  borderRadius: "50%",
                  background: "rgba(0,245,255,.04)",
                  filter: "blur(30px)",
                  pointerEvents: "none",
                }}
              />
              <span
                style={{
                  display: "inline-block",
                  background: "rgba(0,245,255,.15)",
                  color: "#22d3ee",
                  fontSize: "clamp(0.55rem, 1.2vw, 0.65rem)",
                  fontWeight: 700,
                  padding: "3px 10px",
                  borderRadius: 100,
                  marginBottom: "clamp(0.75rem, 2vw, 0.75rem)",
                  letterSpacing: ".12em",
                  textTransform: "uppercase",
                  fontFamily: "sans-serif",
                  width: "fit-content",
                }}
              >
                Banking
              </span>
              <h3
                style={{
                  fontFamily: "'Orbitron',monospace",
                  fontSize: "clamp(1.4rem, 3vw, 1.8rem)",
                  fontWeight: 900,
                  marginBottom: "clamp(0.5rem, 1vw, 0.5rem)",
                }}
              >
                <span style={{ color: "#22d3ee" }}>Fin</span>
                <span style={{ color: "#fff" }}>Vibe</span>
              </h3>
              <p
                style={{
                  color: "rgba(255,255,255,.65)",
                  fontSize: "clamp(0.75rem, 1.5vw, 0.85rem)",
                  lineHeight: 1.75,
                  marginBottom: "clamp(1.25rem, 3vw, 1.5rem)",
                  fontFamily: "sans-serif",
                }}
              >
                {FINVIBE_DESC[selectedCountry]}
              </p>
              <ul
                style={{
                  listStyle: "none",
                  padding: 0,
                  margin: "0 0 clamp(1rem, 2vw, 1.25rem)",
                  display: "flex",
                  flexDirection: "column",
                  gap: "clamp(0.5rem, 1vw, 0.5rem)",
                  flex: 1,
                }}
              >
                {FINVIBE_FEATURES[selectedCountry].map((f, i) => (
                  <li
                    key={i}
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "clamp(0.6rem, 1.5vw, 0.75rem)",
                      fontSize: "clamp(0.72rem, 1.3vw, 0.83rem)",
                      color: "rgba(255,255,255,.8)",
                      fontFamily: "sans-serif",
                      lineHeight: 1.6,
                    }}
                  >
                    <span
                      style={{ color: "#22d3ee", flexShrink: 0, marginTop: 4 }}
                    >
                      ◆
                    </span>
                    {f}
                  </li>
                ))}
              </ul>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "clamp(0.4rem, 1vw, 0.375rem)",
                  paddingTop: "clamp(1rem, 2vw, 1rem)",
                  borderTop: "1px solid rgba(255,255,255,.06)",
                }}
              >
                {FINVIBE_TAGS[selectedCountry].map((tag) => (
                  <span
                    key={tag}
                    style={{
                      fontSize: "clamp(0.6rem, 1vw, 0.68rem)",
                      background: "rgba(0,245,255,.08)",
                      border: "1px solid rgba(0,245,255,.2)",
                      color: "#22d3ee",
                      padding: "3px 10px",
                      borderRadius: 8,
                      fontFamily: "sans-serif",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            {/* InsurVibe */}
            <div
              style={{
                background:
                  "linear-gradient(135deg,rgba(168,85,247,.09),rgba(99,102,241,.06))",
                border: "1px solid rgba(168,85,247,.25)",
                borderRadius: 20,
                padding: "clamp(1.75rem, 5vw, 2.25rem)",
                display: "flex",
                flexDirection: "column",
                position: "relative",
                overflow: "hidden",
                transition: "all .35s ease",
                boxShadow: "0 0 30px rgba(168,85,247,.08)",
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.borderColor =
                  "rgba(168,85,247,.4)";
                (e.currentTarget as HTMLElement).style.boxShadow =
                  "0 0 40px rgba(168,85,247,.16)";
                (e.currentTarget as HTMLElement).style.transform =
                  "translateY(-4px)";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.borderColor =
                  "rgba(168,85,247,.25)";
                (e.currentTarget as HTMLElement).style.boxShadow =
                  "0 0 30px rgba(168,85,247,.08)";
                (e.currentTarget as HTMLElement).style.transform =
                  "translateY(0)";
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: -40,
                  right: -40,
                  width: 160,
                  height: 160,
                  borderRadius: "50%",
                  background: "rgba(168,85,247,.04)",
                  filter: "blur(30px)",
                  pointerEvents: "none",
                }}
              />
              <span
                style={{
                  display: "inline-block",
                  background: "rgba(168,85,247,.15)",
                  color: "#c084fc",
                  fontSize: "clamp(0.55rem, 1.2vw, 0.65rem)",
                  fontWeight: 700,
                  padding: "3px 10px",
                  borderRadius: 100,
                  marginBottom: "clamp(0.75rem, 2vw, 0.75rem)",
                  letterSpacing: ".12em",
                  textTransform: "uppercase",
                  fontFamily: "sans-serif",
                  width: "fit-content",
                }}
              >
                Insurance
              </span>
              <h3
                style={{
                  fontFamily: "'Orbitron',monospace",
                  fontSize: "clamp(1.4rem, 3vw, 1.8rem)",
                  fontWeight: 900,
                  marginBottom: "clamp(0.5rem, 1vw, 0.5rem)",
                }}
              >
                <span style={{ color: "#c084fc" }}>Insur</span>
                <span style={{ color: "#fff" }}>Vibe</span>
              </h3>
              <p
                style={{
                  color: "rgba(255,255,255,.65)",
                  fontSize: "clamp(0.75rem, 1.5vw, 0.85rem)",
                  lineHeight: 1.75,
                  marginBottom: "clamp(1.25rem, 3vw, 1.5rem)",
                  fontFamily: "sans-serif",
                }}
              >
                {INSURVIBE_DESC[selectedCountry]}
              </p>
              <ul
                style={{
                  listStyle: "none",
                  padding: 0,
                  margin: "0 0 clamp(1rem, 2vw, 1.25rem)",
                  display: "flex",
                  flexDirection: "column",
                  gap: "clamp(0.5rem, 1vw, 0.5rem)",
                  flex: 1,
                }}
              >
                {INSURVIBE_FEATURES[selectedCountry].map((f, i) => (
                  <li
                    key={i}
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: "clamp(0.6rem, 1.5vw, 0.75rem)",
                      fontSize: "clamp(0.72rem, 1.3vw, 0.83rem)",
                      color: "rgba(255,255,255,.8)",
                      fontFamily: "sans-serif",
                      lineHeight: 1.6,
                    }}
                  >
                    <span
                      style={{ color: "#c084fc", flexShrink: 0, marginTop: 4 }}
                    >
                      ◆
                    </span>
                    {f}
                  </li>
                ))}
              </ul>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "clamp(0.4rem, 1vw, 0.375rem)",
                  paddingTop: "clamp(1rem, 2vw, 1rem)",
                  borderTop: "1px solid rgba(255,255,255,.06)",
                }}
              >
                {INSURVIBE_TAGS[selectedCountry].map((tag) => (
                  <span
                    key={tag}
                    style={{
                      fontSize: "clamp(0.6rem, 1vw, 0.68rem)",
                      background: "rgba(168,85,247,.08)",
                      border: "1px solid rgba(168,85,247,.2)",
                      color: "#c084fc",
                      padding: "3px 10px",
                      borderRadius: 8,
                      fontFamily: "sans-serif",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── AI USE CASES ENGINE SECTION */}
      <section
        style={{
          position: "relative",
          zIndex: 2,
          padding: "clamp(4rem, 10vw, 5rem) clamp(1rem, 5vw, 2.5rem)",
          borderTop: "1px solid rgba(108,92,231,.15)",
          background: "linear-gradient(135deg,rgba(108,92,231,.06) 0%,rgba(0,184,148,.04) 100%)",
        }}
      >
        <div style={{ maxWidth: 1080, margin: "0 auto" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "clamp(2rem, 5vw, 3rem)" }}>

            {/* Left: text */}
            <div style={{ flex: "1 1 420px", minWidth: 0 }}>
              <div style={{
                display: "inline-flex", alignItems: "center", gap: 8,
                background: "rgba(0,184,148,.1)", border: "1px solid rgba(0,184,148,.3)",
                borderRadius: 100, padding: "4px 16px", marginBottom: 20,
              }}>
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#00cec9", animation: "pulseGlow 2s infinite", display: "inline-block" }} />
                <span style={{ fontSize: "clamp(0.58rem, 1vw, 0.68rem)", fontWeight: 700, letterSpacing: ".16em", color: "#00cec9", fontFamily: "'Orbitron',monospace", textTransform: "uppercase" as const }}>LIVE AI DEMO</span>
              </div>
              <h2 style={{
                fontFamily: "'Orbitron',monospace",
                fontSize: "clamp(1.4rem, 4vw, 2.4rem)",
                fontWeight: 900, lineHeight: 1.1,
                marginBottom: 16, letterSpacing: "-.02em",
              }}>
                <span style={{ color: "#fff" }}>30+ Real AI</span><br />
                <span style={{
                  background: "linear-gradient(90deg,#6C5CE7,#00B894)",
                  WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
                }}>Use Cases in Action</span>
              </h2>
              <p style={{
                color: "rgba(255,255,255,.6)", fontSize: "clamp(0.82rem, 1.5vw, 0.95rem)",
                lineHeight: 1.75, marginBottom: 28, fontFamily: "sans-serif", maxWidth: 460,
              }}>
                Watch AI transform real banking operations — Customer Onboarding, Loan Eligibility, Underwriting — from manual input to live executable code in under 2 seconds.
              </p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 32 }}>
                {[
                  { icon: "⚙️", label: "15-Step Engine" },
                  { icon: "🤖", label: "50 AI Agents" },
                  { icon: "</>", label: "Code Generation" },
                  { icon: "▶",  label: "Live Execution" },
                ].map(b => (
                  <div key={b.label} style={{
                    display: "flex", alignItems: "center", gap: 6,
                    padding: "7px 14px", borderRadius: 9,
                    background: "rgba(108,92,231,.12)", border: "1px solid rgba(108,92,231,.28)",
                  }}>
                    <span style={{ fontSize: 14 }}>{b.icon}</span>
                    <span style={{ color: "#c4b5fd", fontSize: "clamp(0.7rem, 1.2vw, 0.78rem)", fontWeight: 600, fontFamily: "sans-serif" }}>{b.label}</span>
                  </div>
                ))}
              </div>
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                <button
                  onClick={() => navigate("/use-case-engine")}
                  style={{
                    padding: "clamp(0.85rem, 2vw, 1rem) clamp(1.75rem, 3.5vw, 2rem)",
                    borderRadius: 12, border: "1px solid rgba(0,184,148,.55)",
                    background: "linear-gradient(135deg,rgba(0,184,148,.18),rgba(108,92,231,.12))",
                    color: "#00cec9", fontFamily: "'Orbitron',monospace",
                    fontWeight: 700, fontSize: "clamp(0.7rem, 1.2vw, 0.8rem)",
                    letterSpacing: ".1em", cursor: "pointer",
                    boxShadow: "0 2px 24px rgba(0,184,148,.2)", transition: "all .3s",
                  }}
                  onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-3px)"; e.currentTarget.style.boxShadow = "0 8px 32px rgba(0,184,148,.3)"; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 2px 24px rgba(0,184,148,.2)"; }}
                >
                  Explore Use Cases →
                </button>
                {/* <button
                  onClick={() => navigate("/live-ai-demo")}
                  style={{
                    padding: "clamp(0.85rem, 2vw, 1rem) clamp(1.75rem, 3.5vw, 2rem)",
                    borderRadius: 12, border: "1px solid rgba(108,92,231,.45)",
                    background: "transparent",
                    color: "#c4b5fd", fontFamily: "'Orbitron',monospace",
                    fontWeight: 700, fontSize: "clamp(0.7rem, 1.2vw, 0.8rem)",
                    letterSpacing: ".1em", cursor: "pointer", transition: "all .3s",
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = "rgba(108,92,231,.12)"; e.currentTarget.style.transform = "translateY(-3px)"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.transform = "translateY(0)"; }}
                >
                  ▶ Live AI Demo
                </button> */}
              </div>
            </div>

            {/* Right: use case flow cards */}
            <div style={{ flex: "1 1 340px", minWidth: 0, display: "flex", flexDirection: "column", gap: 12 }}>
              {[
                { icon: "👤", title: "Customer Onboarding", sub: "KYC · AML · Identity Verification",      color: "#6C5CE7", steps: ["Run Engine", "4 Agents", "Decision", "Code", "✅ Customer Created"] },
                { icon: "🏦", title: "Loan Eligibility",     sub: "Credit Score · DTI · Risk Analysis",      color: "#00B894", steps: ["Credit Analysis", "Decision", "Code", "✅ AED 250,000 Approved"] },
                { icon: "📋", title: "Underwriting",         sub: "Policy Assessment · Risk Rating",          color: "#E17055", steps: ["Risk Analysis", "Actuarial", "Code", "✅ Policy Approved"] },
              ].map((uc, i) => (
                <div key={i} style={{
                  padding: "16px 20px", borderRadius: 14,
                  background: `linear-gradient(135deg,${uc.color}12,${uc.color}06)`,
                  border: `1px solid ${uc.color}35`,
                  transition: "all .25s",
                }}
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = `${uc.color}70`; (e.currentTarget as HTMLElement).style.transform = "translateX(6px)"; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = `${uc.color}35`; (e.currentTarget as HTMLElement).style.transform = "translateX(0)"; }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                    <span style={{ fontSize: 20 }}>{uc.icon}</span>
                    <div>
                      <div style={{ color: "#fff", fontWeight: 700, fontSize: "clamp(0.82rem, 1.4vw, 0.9rem)", fontFamily: "sans-serif" }}>{uc.title}</div>
                      <div style={{ color: "rgba(255,255,255,.5)", fontSize: "clamp(0.65rem, 1vw, 0.72rem)", fontFamily: "sans-serif" }}>{uc.sub}</div>
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap" }}>
                    {uc.steps.map((s, j) => (
                      <React.Fragment key={j}>
                        <span style={{
                          padding: "3px 9px", borderRadius: 6, fontSize: "clamp(0.6rem, 1vw, 0.68rem)", fontWeight: 600,
                          background: s.startsWith("✅") ? `${uc.color}25` : "rgba(255,255,255,.06)",
                          color: s.startsWith("✅") ? uc.color : "rgba(255,255,255,.65)",
                          border: `1px solid ${s.startsWith("✅") ? uc.color + "50" : "rgba(255,255,255,.1)"}`,
                          fontFamily: "sans-serif",
                        }}>{s}</span>
                        {j < uc.steps.length - 1 && <span style={{ color: "rgba(255,255,255,.25)", fontSize: 11 }}>→</span>}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── ROADMAP */}
      <RoadmapSection country={selectedCountry} />

      {/* ── FEATURES */}
      <section
        style={{
          position: "relative",
          zIndex: 2,
          padding: "clamp(5rem, 12vw, 6rem) clamp(1rem, 5vw, 2.5rem)",
          borderTop: "1px solid rgba(0,245,255,.06)",
        }}
      >
        <div style={{ maxWidth: 1080, margin: "0 auto" }}>
          <div
            style={{
              textAlign: "center",
              marginBottom: "clamp(2.5rem, 5vw, 3rem)",
            }}
          >
            <h2
              style={{
                fontFamily: "'Orbitron',monospace",
                fontSize: "clamp(1rem, 3vw, 1.9rem)",
                fontWeight: 800,
                color: "#fff",
                marginBottom: "clamp(0.5rem, 1.5vw, 0.625rem)",
              }}
            >
              Platform Capabilities
            </h2>
            <p
              style={{
                color: "rgba(255,255,255,.38)",
                fontSize: "clamp(0.75rem, 1.5vw, 0.85rem)",
                maxWidth: 460,
                margin: "0 auto",
              }}
            >
              Built for enterprise-grade performance with intelligent automation
              at its core
            </p>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit,minmax(260px,1fr))",
              gap: "clamp(1rem, 2.5vw, 1.5rem)",
            }}
          >
            {(
              [
                {
                  icon: "🧠",
                  title: "Neural Analytics",
                  desc: "Deep learning models trained on financial and insurance data for predictive intelligence.",
                  color: "#00f5ff",
                },
                {
                  icon: "⚡",
                  title: "Real-Time Processing",
                  desc: "Sub-millisecond transaction analysis with intelligent anomaly detection at scale.",
                  color: "#7c3aed",
                },
                {
                  icon: "🔐",
                  title: "Compliance Engine",
                  desc: "253+ master directions mapped and automated for end-to-end regulatory compliance.",
                  color: "#06b6d4",
                },
                {
                  icon: "📊",
                  title: "Report Generation",
                  desc: "Generate regulatory, compliance, audit, and MIS reports with AI-powered automation.",
                  color: "#00f5ff",
                },
                {
                  icon: "📊",
                  title: "Smart Dashboard",
                  desc: "Holographic-grade UI with live KPIs, risk heat maps, and AI-generated insights.",
                  color: "#7C3AED",
                },
                {
                  icon: "🤖",
                  title: "AI Automation",
                  desc: "Intelligent RPA workflows for policy processing, claims routing, and reconciliation.",
                  color: "#00f5ff",
                },
                {
                  icon: "🌐",
                  title: "API Ecosystem",
                  desc: "Open banking and insurance APIs with real-time webhooks and enterprise integrations.",
                  color: "#7c3aed",
                },
                {
                  icon: "🧪",
                  title: "Auto Test Cases",
                  desc: "AI generates exhaustive unit, integration, and edge-case test suites from your codebase — zero manual effort.",
                  color: "#10b981",
                },
                {
                  icon: "🗂️",
                  title: "Auto Test Data",
                  desc: "Synthesises realistic, regulation-compliant test datasets for banking and insurance scenarios on demand.",
                  color: "#f59e0b",
                },
              ] as {
                icon: string;
                title: string;
                desc: string;
                color: string;
              }[]
            ).map((feat, i) => (
              <TiltCard key={i}>
                <div
                  style={{
                    background:
                      "linear-gradient(135deg,rgba(255,255,255,.032),rgba(124,58,237,.02))",
                    border: "1px solid rgba(255,255,255,.08)",
                    borderRadius: 16,
                    padding: "clamp(1.25rem, 3vw, 1.75rem)",
                    transition: "all .3s cubic-bezier(0.4,0,0.2,1)",
                    cursor: "pointer",
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLDivElement).style.borderColor =
                      `${feat.color}45`;
                    (e.currentTarget as HTMLDivElement).style.boxShadow =
                      `0 8px 32px ${feat.color}18`;
                    (e.currentTarget as HTMLDivElement).style.transform =
                      "translateY(-6px)";
                    (e.currentTarget as HTMLDivElement).style.background =
                      `linear-gradient(135deg,rgba(255,255,255,.06),${feat.color}08)`;
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLDivElement).style.borderColor =
                      "rgba(255,255,255,.08)";
                    (e.currentTarget as HTMLDivElement).style.boxShadow =
                      "none";
                    (e.currentTarget as HTMLDivElement).style.transform =
                      "translateY(0)";
                    (e.currentTarget as HTMLDivElement).style.background =
                      "linear-gradient(135deg,rgba(255,255,255,.032),rgba(124,58,237,.02))";
                  }}
                >
                  <div
                    style={{
                      fontSize: "clamp(1.2rem, 3vw, 1.75rem)",
                      marginBottom: 14,
                    }}
                  >
                    {feat.icon}
                  </div>
                  <h3
                    style={{
                      fontFamily: "'Orbitron',monospace",
                      fontWeight: 700,
                      fontSize: "clamp(0.8rem, 1.5vw, 0.95rem)",
                      color: feat.color,
                      marginBottom: 10,
                      letterSpacing: ".04em",
                    }}
                  >
                    {feat.title}
                  </h3>
                  <p
                    style={{
                      color: "rgba(255,255,255,.55)",
                      fontSize: "clamp(0.75rem, 1.2vw, 0.82rem)",
                      lineHeight: 1.7,
                    }}
                  >
                    {feat.desc}
                  </p>
                </div>
              </TiltCard>
            ))}
          </div>
        </div>
      </section>

      <section
        style={{
          position: "relative",
          zIndex: 2,
          padding: "clamp(3rem, 8vw, 4rem) clamp(1rem, 5vw, 2.5rem)",
        }}
      >
        <div style={{ maxWidth: 1080, margin: "0 auto" }}>
          <div
            style={{
              background:
                "linear-gradient(135deg,rgba(0,245,255,.08) 0%,rgba(124,58,237,.09) 50%,rgba(168,85,247,.08) 100%)",
              border: "1px solid rgba(0,245,255,.22)",
              borderRadius: 18,
              padding:
                "clamp(1.75rem, 5vw, 2.5rem) clamp(1.75rem, 5vw, 2.5rem)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "clamp(1.25rem, 4vw, 2rem)",
              flexWrap: "wrap" as const,
              boxShadow: "0 4px 40px rgba(0,245,255,.08)",
              transition: "all .35s ease",
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.borderColor =
                "rgba(0,245,255,.35)";
              (e.currentTarget as HTMLElement).style.boxShadow =
                "0 8px 50px rgba(0,245,255,.12)";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.borderColor =
                "rgba(0,245,255,.22)";
              (e.currentTarget as HTMLElement).style.boxShadow =
                "0 4px 40px rgba(0,245,255,.08)";
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "clamp(1rem, 3vw, 1rem)",
              }}
            >
              <div
                style={{
                  width: "clamp(40px, 8vw, 48px)",
                  height: "clamp(40px, 8vw, 48px)",
                  borderRadius: 13,
                  flexShrink: 0,
                  background:
                    "linear-gradient(135deg,rgba(0,245,255,.18),rgba(124,58,237,.18))",
                  border: "1px solid rgba(0,245,255,.28)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "clamp(1.2rem, 3vw, 1.5rem)",
                }}
              >
                📦
              </div>
              <div style={{ minWidth: 0 }}>
                <p
                  style={{
                    fontSize: "clamp(0.55rem, 1vw, 0.68rem)",
                    letterSpacing: ".16em",
                    textTransform: "uppercase" as const,
                    color: "rgba(0,245,255,.7)",
                    fontFamily: "'Orbitron',monospace",
                    fontWeight: 600,
                    margin: "0 0 4px",
                  }}
                >
                  Community Showcase
                </p>
                <h3
                  style={{
                    fontFamily: "'Orbitron',monospace",
                    fontWeight: 800,
                    fontSize: "clamp(0.8rem, 2vw, 1.2rem)",
                    color: "#fff",
                    margin: "0 0 5px",
                    letterSpacing: "-.01em",
                  }}
                >
                  Explore OXYBFSAI Use Cases
                </h3>
                <p
                  style={{
                    fontSize: "clamp(0.7rem, 1.2vw, 0.8rem)",
                    color: "rgba(255,255,255,.45)",
                    margin: 0,
                    lineHeight: 1.6,
                    fontFamily: "sans-serif",
                  }}
                >
                  Browse real BFSI apps built by{" "}
                  <span style={{ color: "#FFFFFF" }}>OXY</span>
                  <span style={{ color: "#00D4FF" }}>BFS</span>
                  <span style={{ color: "#7C3AED" }}>AI</span> — full backend,
                  frontend &amp; database code, ready to use.
                </p>
              </div>
            </div>
            <button
              onClick={() =>
                window.open("https://vibecoding-finvibe.vercel.app/", "_blank")
              }
              style={{
                padding:
                  "clamp(0.85rem, 2vw, 1rem) clamp(1.75rem, 3.5vw, 2rem)",
                borderRadius: 12,
                border: "1px solid rgba(0,245,255,.55)",
                background:
                  "linear-gradient(135deg,rgba(0,245,255,.16),rgba(124,58,237,.12))",
                color: "#00f5ff",
                fontFamily: "'Orbitron',monospace",
                fontWeight: 700,
                fontSize: "clamp(0.7rem, 1.2vw, 0.82rem)",
                letterSpacing: ".1em",
                cursor: "pointer",
                transition: "all .3s cubic-bezier(0.4,0,0.2,1)",
                whiteSpace: "nowrap" as const,
                flexShrink: 0,
                boxShadow: "0 2px 24px rgba(0,245,255,.15)",
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.background =
                  "linear-gradient(135deg,rgba(0,245,255,.28),rgba(124,58,237,.2))";
                (e.currentTarget as HTMLElement).style.transform =
                  "translateY(-2px)";
                (e.currentTarget as HTMLElement).style.boxShadow =
                  "0 6px 28px rgba(0,245,255,.22)";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.background =
                  "linear-gradient(135deg,rgba(0,245,255,.14),rgba(124,58,237,.1))";
                (e.currentTarget as HTMLElement).style.transform =
                  "translateY(0)";
                (e.currentTarget as HTMLElement).style.boxShadow =
                  "0 0 20px rgba(0,245,255,.1)";
              }}
            >
              Let's Start →
            </button>
          </div>
        </div>
      </section>

      {/* ── CTA */}
      <section
        style={{
          position: "relative",
          zIndex: 2,
          padding: "clamp(5rem, 12vw, 6rem) clamp(1rem, 5vw, 2.5rem)",
          textAlign: "center",
          background:
            "linear-gradient(135deg,rgba(0,245,255,.06) 0%,rgba(124,58,237,.08) 50%,rgba(168,85,247,.06) 100%)",
          borderTop: "1px solid rgba(0,245,255,.12)",
          borderBottom: "1px solid rgba(168,85,247,.12)",
          backdropFilter: "blur(10px)",
        }}
      >
        <div
          style={{
            fontFamily: "'Orbitron',monospace",
            fontSize: "clamp(0.5rem, 1.2vw, 0.65rem)",
            letterSpacing: ".2em",
            color: "rgba(255,255,255,.28)",
            marginBottom: "clamp(0.75rem, 2vw, 0.75rem)",
          }}
        >
          BEGIN YOUR TRANSFORMATION
        </div>
        <h2
          style={{
            fontFamily: "'Orbitron',monospace",
            fontSize: "clamp(1.2rem, 5vw, 2.7rem)",
            fontWeight: 900,
            background:
              "linear-gradient(90deg,#00f5ff 0%,#fff 50%,#7C3AED 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            marginBottom: "clamp(1rem, 2vw, 1rem)",
          }}
        >
          Ready to Redefine FinTech?
        </h2>
        <p
          style={{
            color: "rgba(255,255,255,.55)",
            fontSize: "clamp(0.8rem, 1.6vw, 0.95rem)",
            maxWidth: 520,
            margin: "0 auto clamp(2rem, 4vw, 2.5rem)",
            lineHeight: 1.8,
          }}
        >
          Join leading banks and insurers leveraging{" "}
          <span style={{ color: "#FFFFFF" }}>OXY</span>
          <span style={{ color: "#00D4FF" }}>BFS</span>
          <span style={{ color: "#7C3AED" }}>AI</span> to build the future of
          intelligent financial services.
        </p>
        <div
          style={{
            display: "flex",
            gap: "clamp(1rem, 3vw, 1.25rem)",
            justifyContent: "center",
            flexWrap: "wrap",
          }}
        >
          {(
            [
              {
                label: "Get Started with Banking",
                bg: "transparent",
                color: "#00f5ff",
                border: "1px solid rgba(0,245,255,.5)",
                shadow: "none",
              },
              {
                label: "Get Started with Insurance",
                bg: "transparent",
                color: "#7C3AED",
                border: "1px solid rgba(168,85,247,.5)",
                shadow: "none",
              },
            ] as {
              label: string;
              bg: string;
              color: string;
              border?: string;
              shadow: string;
            }[]
          ).map((btn, i) => (
            <button
              key={i}
              style={{
                padding:
                  "clamp(0.85rem, 2vw, 1rem) clamp(1.75rem, 3.5vw, 2.25rem)",
                borderRadius: 12,
                background: btn.bg,
                color: btn.color,
                border: btn.border ?? "none",
                fontFamily: "'Orbitron',monospace",
                fontWeight: 700,
                fontSize: "clamp(0.75rem, 1.3vw, 0.85rem)",
                cursor: "pointer",
                boxShadow: `0 2px 20px ${btn.color}20`,
                letterSpacing: ".1em",
                transition: "all .3s cubic-bezier(0.4,0,0.2,1)",
              }}
              onClick={() => {
                if (btn.label === "Get Started with Banking") {
                  handleNavigation(navigate, "/finvibe-code-builder");
                } else if (btn.label === "Get Started with Insurance") {
                  handleNavigation(navigate, "/insurvibe-code-builder");
                }
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform =
                  "translateY(-4px) scale(1.03)";
                e.currentTarget.style.boxShadow = `0 8px 32px ${btn.color}35`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0) scale(1)";
                e.currentTarget.style.boxShadow = `0 2px 20px ${btn.color}20`;
              }}
            >
              {btn.label}
            </button>
          ))}
        </div>
      </section>

      {/* ── FOOTER */}
      <footer
        style={{
          position: "relative",
          zIndex: 2,
          padding: "clamp(2.5rem, 5vw, 3.5rem) clamp(1rem, 5vw, 2.5rem)",
          borderTop: "1px solid rgba(0,245,255,.1)",
          background:
            "linear-gradient(135deg,rgba(0,245,255,.03) 0%,rgba(124,58,237,.05) 100%)",
        }}
      >
        <div
          style={{
            maxWidth: 1050,
            margin: "0 auto",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 16,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
            <div
              style={{
                width: 26,
                height: 26,
                borderRadius: 7,
                background: "linear-gradient(135deg,#00f5ff,#7c3aed)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: ".78rem",
              }}
            >
              ⬡
            </div>
            <span
              style={{
                fontFamily: "'Orbitron',monospace",
                fontWeight: 700,
                fontSize: ".85rem",
              }}
            >
              <span style={{ color: "#FFFFFF" }}>OXY</span>
              <span style={{ color: "#00D4FF" }}>BFS</span>
              <span style={{ color: "#7C3AED" }}>AI</span>
            </span>
          </div>
          <div
            style={{
              color: "rgba(255,255,255,.7)",
              fontSize: "clamp(0.7rem, 1vw, 0.78rem)",
              letterSpacing: ".08em",
              fontWeight: 500,
            }}
          >
            ⚡ Powered by ASKOXY.AI
          </div>
        </div>
        <div
          style={{
            maxWidth: 1050,
            margin: "clamp(1.25rem, 3vw, 1.75rem) auto 0",
            borderTop: "1px solid rgba(0,245,255,.08)",
            paddingTop: 14,
            display: "flex",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 12,
          }}
        >
          <span
            style={{
              fontSize: "clamp(0.65rem, 1vw, 0.75rem)",
              color: "rgba(255,255,255,.65)",
            }}
          >
            © 2026 <span style={{ color: "#FFFFFF" }}>OXY</span>
            <span style={{ color: "#00D4FF" }}>BFS</span>
            <span style={{ color: "#7C3AED" }}>AI</span> — All rights reserved
          </span>
          {/* <a href="#" style={{fontSize:".68rem",color:"rgba(255,255,255,.25)",textDecoration:"none"}}>contact@oxybfs.ai</a> */}
        </div>
      </footer>
    </div>
  );
}
